import httpService, { HttpError } from "./httpService";

export type CommentProductType = "BASKET" | "PORTFOLIO";
export type CommentStatus = "PENDING" | "APPROVED" | "DELETED";

// One comment or reply as returned by the read/create endpoints. Internal
// moderation columns (deletedByUserId, approvedAtUtc) are omitted by the API.
export interface ProductComment {
  id: number;
  parentId: number | null;
  productType: CommentProductType;
  productId: number;
  body: string;
  status: CommentStatus;
  alias: string | null;
  // Added by PWA-2356. Optional during rolling deployment so comments from an
  // older backend response remain valid and simply render without the badge.
  isCustomer?: boolean;
  createdAtUtc: string;
}

// Admin-only comment shape. Public comment endpoints intentionally omit the
// stable author identifier.
export interface AdminProductComment extends ProductComment {
  userId: string;
}

// A root comment in a listing, carrying the count of replies visible to the
// caller (APPROVED plus the caller's own PENDING). Replies are fetched lazily.
export interface RootComment extends ProductComment {
  replyCount: number;
}

export interface PaginatedRootComments {
  currentPage: number;
  itemsOnPage: number;
  totalPages: number;
  // Count of root threads visible to the caller (drives pagination; excludes replies).
  totalItems: number;
  // Total comments visible to the caller for this product INCLUDING threaded
  // replies (roots + replies). Independent of pageSize.
  totalCommentCount: number;
  items: RootComment[];
}

export interface PaginatedComments {
  currentPage: number;
  itemsOnPage: number;
  totalPages: number;
  totalItems: number;
  items: ProductComment[];
}

export interface PaginatedAdminComments extends Omit<PaginatedComments, "items"> {
  items: AdminProductComment[];
}

// Optional filters for the admin moderation list. Omitted fields are not sent,
// so the backend returns the unfiltered list (all statuses, all products).
export interface AdminCommentFilters {
  status?: CommentStatus;
  productType?: CommentProductType;
  productId?: number;
}

// Typed error so hooks can branch on 404 (product/parent not visible) vs other
// failures without re-parsing the raw HttpError shape at every call site.
export class CommentApiError extends Error {
  readonly status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = "CommentApiError";
    this.status = status;
  }
}

function toCommentApiError(error: unknown): CommentApiError {
  const httpError = error as HttpError;
  const status = httpError?.status ?? httpError?.response?.status;
  const message = httpError instanceof Error ? httpError.message : "Comment request failed";
  return new CommentApiError(message, status);
}

class ProductCommentService {
  // Paginated root comments for a product, newest first. Each item carries a
  // replyCount; replies are loaded lazily via listReplies.
  async listComments(
    productType: CommentProductType,
    productId: number,
    page: number,
    pageSize: number
  ): Promise<PaginatedRootComments> {
    try {
      const response = await httpService.get<PaginatedRootComments>(
        `v3/products/comments/${productType}/${productId}?page=${page}&pageSize=${pageSize}`
      );
      return response.data;
    } catch (error) {
      throw toCommentApiError(error);
    }
  }

  // Paginated replies for one root comment, oldest first (chronological thread).
  async listReplies(
    productType: CommentProductType,
    productId: number,
    rootId: number,
    page: number,
    pageSize: number
  ): Promise<PaginatedComments> {
    try {
      const response = await httpService.get<PaginatedComments>(
        `v3/products/comments/${productType}/${productId}/replies/${rootId}?page=${page}&pageSize=${pageSize}`
      );
      return response.data;
    } catch (error) {
      throw toCommentApiError(error);
    }
  }

  // Create a root comment (parentId omitted/null) or a reply (parentId set).
  // Returns the created row in PENDING status, under the author's alias.
  async createComment(
    productType: CommentProductType,
    productId: number,
    body: string,
    parentId?: number | null
  ): Promise<ProductComment> {
    try {
      const response = await httpService.post<ProductComment>(
        `v3/products/comments/${productType}/${productId}`,
        parentId != null ? { body, parentId } : { body }
      );
      return response.data;
    } catch (error) {
      throw toCommentApiError(error);
    }
  }

  // Admin moderation list across all statuses (including DELETED), newest first.
  // Optional status/productType/productId filters narrow the result. Requires the
  // ADMIN role server-side (403 otherwise). Items here are flat comments (roots
  // and replies), distinguished by parentId.
  async listCommentsForModeration(
    filters: AdminCommentFilters,
    page: number,
    pageSize: number
  ): Promise<PaginatedAdminComments> {
    const query = new URLSearchParams();
    query.set("page", String(page));
    query.set("pageSize", String(pageSize));
    if (filters.status) query.set("status", filters.status);
    if (filters.productType) query.set("productType", filters.productType);
    if (filters.productId != null) query.set("productId", String(filters.productId));
    try {
      const response = await httpService.get<PaginatedAdminComments>(
        `v3/admin/comments?${query.toString()}`
      );
      return response.data;
    } catch (error) {
      throw toCommentApiError(error);
    }
  }

  // Admin soft-delete of any comment or reply. Deleting a root also soft-deletes
  // its replies. Requires the ADMIN role server-side. Returns nothing on success.
  async adminDeleteComment(commentId: number): Promise<void> {
    try {
      await httpService.delete(`v3/admin/comments/${commentId}`);
    } catch (error) {
      throw toCommentApiError(error);
    }
  }
}

export const productCommentService = new ProductCommentService();
